export type TooltipPosition = 'below' | 'above' | 'left' | 'right';
export type ArrowDirection = 'up' | 'down' | 'left' | 'right';
/**
 * Controls alignment along the axis perpendicular to the tooltip's side.
 * For 'below'/'above': horizontal alignment relative to the trigger.
 * For 'left'/'right': vertical alignment relative to the trigger.
 *   'start'  — align the near edges (left for below/above, top for left/right)
 *   'center' — center the tooltip on the trigger's midpoint
 *   'end'    — align the far edges (right for below/above, bottom for left/right)
 * null defers to the existing per-side default ('end' for below/above, 'center' for left/right).
 */
export type TooltipAlign = 'start' | 'center' | 'end';

export interface TooltipPositionResult {
  tooltipStyle: Record<string, string>;
  arrowStyle: Record<string, string>;
  arrowDirection: ArrowDirection;
}

// Arrow triangle dimensions (px). The arrow is a CSS border triangle:
// 10px wide (ARROW_HALF_W × 2) and 5px tall (GAP), so centering uses half-width.
const ARROW_HALF_W = 5;
export const GAP = 5; // space between the arrow tip and the trigger edge
export const MARGIN = 8; // minimum gap between tooltip and viewport edges

// Priority order to try when flipping: preferred side first, then opposite,
// then the perpendicular axis.
const FLIP_ORDER: Record<TooltipPosition, TooltipPosition[]> = {
  below: ['below', 'above', 'right', 'left'],
  above: ['above', 'below', 'right', 'left'],
  right: ['right', 'left', 'below', 'above'],
  left:  ['left', 'right', 'below', 'above'],
};

/**
 * Returns whether a given placement side has enough room in the viewport.
 * Only checks the axis that matters per side (vertical for above/below,
 * horizontal for left/right).
 */
export function fitsInViewport(
  side: TooltipPosition,
  rect: DOMRect,
  maxWidth: number,
  estimatedHeight: number,
  margin: number,
  gap: number,
): boolean {
  switch (side) {
    case 'below':
      return rect.bottom + gap + estimatedHeight + margin <= window.innerHeight;
    case 'above':
      return rect.top - gap - estimatedHeight - margin >= 0;
    case 'right':
      return rect.right + gap + maxWidth + margin <= window.innerWidth;
    case 'left':
      return rect.left - gap - maxWidth - margin >= 0;
  }
}

/**
 * Estimates the rendered tooltip height from content length and available width,
 * avoiding a DOM measurement. Used as input to fitsInViewport for flip decisions.
 * Assumes ~7px per character, 20px line-height, 24px vertical padding.
 */
export function estimateTooltipHeight(content: string | object, maxWidth: number): number {
  const contentStr = typeof content === 'string' ? content : String(content);
  const charsPerLine = Math.floor(maxWidth / 7);
  const lines = charsPerLine > 0 ? Math.ceil(contentStr.length / charsPerLine) : 1;
  return lines * 20 + 24;
}

/**
 * Walks the flip priority list for the requested position and returns the first
 * side that fits in the viewport. Falls back to 'below' as a last resort so
 * something always renders even in very constrained viewports.
 */
export function resolvePosition(
  rect: DOMRect,
  maxWidth: number,
  margin: number,
  gap: number,
  position: TooltipPosition,
  content: string | object,
): TooltipPosition {
  const estimatedHeight = estimateTooltipHeight(content, maxWidth);
  const candidates = FLIP_ORDER[position] ?? FLIP_ORDER.below;

  for (const candidate of candidates) {
    if (fitsInViewport(candidate, rect, maxWidth, estimatedHeight, margin, gap)) {
      return candidate;
    }
  }

  return 'below';
}

/**
 * Computes the fixed CSS coordinates and arrow styles for the tooltip given the
 * trigger's bounding rect and constraints. When disableFlip is false, the
 * position may be changed to the first side that fits in the viewport.
 *
 * `align` governs alignment along the cross-axis (see TooltipAlign). Passing
 * null preserves the existing default for each side.
 */
export function calculatePosition(
  rect: DOMRect,
  maxWidth: number,
  margin: number,
  position: TooltipPosition,
  disableFlip: boolean,
  content: string | object,
  align: TooltipAlign | null = null,
): TooltipPositionResult {
  const resolvedPosition = disableFlip
    ? position
    : resolvePosition(rect, maxWidth, margin, GAP, position, content);

  switch (resolvedPosition) {
    // Tooltip sits above the trigger; arrow points downward.
    case 'above': {
      const bottomVal = `${window.innerHeight - rect.top + GAP}px`;
      const effectiveAlign = align ?? 'end';

      if (effectiveAlign === 'center') {
        const cx = rect.left + rect.width / 2;
        const halfAvail = Math.min(cx - margin, window.innerWidth - cx - margin);
        return {
          tooltipStyle: {
            bottom: bottomVal,
            left: `${cx}px`,
            maxWidth: `${Math.min(maxWidth, halfAvail * 2)}px`,
            transform: 'translateX(-50%)',
          },
          arrowStyle: { left: '50%', transform: 'translateX(-50%)' },
          arrowDirection: 'down',
        };
      }

      if (effectiveAlign === 'start') {
        return {
          tooltipStyle: {
            bottom: bottomVal,
            left: `${rect.left}px`,
            maxWidth: `${Math.min(maxWidth, window.innerWidth - rect.left - margin)}px`,
          },
          arrowStyle: { left: `${rect.width / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'down',
        };
      }

      // 'end' — original behaviour: right edge of tooltip aligns with right edge of trigger.
      const rightOffset = Math.max(margin, window.innerWidth - rect.right);
      return {
        tooltipStyle: {
          bottom: bottomVal,
          right: `${rightOffset}px`,
          maxWidth: `${maxWidth}px`,
        },
        arrowStyle: { right: `${rect.width / 2 - ARROW_HALF_W}px` },
        arrowDirection: 'down',
      };
    }

    // Tooltip sits to the right of the trigger; arrow points leftward.
    case 'right': {
      const leftVal = `${rect.right + GAP}px`;
      const clampedMaxWidth = `${Math.min(maxWidth, window.innerWidth - rect.right - margin - GAP)}px`;
      const effectiveAlign = align ?? 'center';

      if (effectiveAlign === 'start') {
        return {
          tooltipStyle: { top: `${rect.top}px`, left: leftVal, maxWidth: clampedMaxWidth },
          arrowStyle: { top: `${rect.height / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'left',
        };
      }

      if (effectiveAlign === 'end') {
        return {
          tooltipStyle: { bottom: `${window.innerHeight - rect.bottom}px`, left: leftVal, maxWidth: clampedMaxWidth },
          arrowStyle: { bottom: `${rect.height / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'left',
        };
      }

      // 'center' — original behaviour: vertically centred on trigger.
      return {
        tooltipStyle: {
          top: `${rect.top + rect.height / 2}px`,
          left: leftVal,
          maxWidth: clampedMaxWidth,
          transform: 'translateY(-50%)',
        },
        arrowStyle: { top: '50%', transform: 'translateY(-50%)' },
        arrowDirection: 'left',
      };
    }

    // Tooltip sits to the left of the trigger; arrow points rightward.
    case 'left': {
      const rightVal = `${window.innerWidth - rect.left + GAP}px`;
      const clampedMaxWidth = `${Math.min(maxWidth, rect.left - margin - GAP)}px`;
      const effectiveAlign = align ?? 'center';

      if (effectiveAlign === 'start') {
        return {
          tooltipStyle: { top: `${rect.top}px`, right: rightVal, maxWidth: clampedMaxWidth },
          arrowStyle: { top: `${rect.height / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'right',
        };
      }

      if (effectiveAlign === 'end') {
        return {
          tooltipStyle: { bottom: `${window.innerHeight - rect.bottom}px`, right: rightVal, maxWidth: clampedMaxWidth },
          arrowStyle: { bottom: `${rect.height / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'right',
        };
      }

      // 'center' — original behaviour: vertically centred on trigger.
      return {
        tooltipStyle: {
          top: `${rect.top + rect.height / 2}px`,
          right: rightVal,
          maxWidth: clampedMaxWidth,
          transform: 'translateY(-50%)',
        },
        arrowStyle: { top: '50%', transform: 'translateY(-50%)' },
        arrowDirection: 'right',
      };
    }

    // Tooltip sits below the trigger (default); arrow points upward.
    default: {
      const topVal = `${rect.bottom + GAP}px`;
      const effectiveAlign = align ?? 'end';

      if (effectiveAlign === 'center') {
        const cx = rect.left + rect.width / 2;
        const halfAvail = Math.min(cx - margin, window.innerWidth - cx - margin);
        return {
          tooltipStyle: {
            top: topVal,
            left: `${cx}px`,
            maxWidth: `${Math.min(maxWidth, halfAvail * 2)}px`,
            transform: 'translateX(-50%)',
          },
          arrowStyle: { left: '50%', transform: 'translateX(-50%)' },
          arrowDirection: 'up',
        };
      }

      if (effectiveAlign === 'start') {
        return {
          tooltipStyle: {
            top: topVal,
            left: `${rect.left}px`,
            maxWidth: `${Math.min(maxWidth, window.innerWidth - rect.left - margin)}px`,
          },
          arrowStyle: { left: `${rect.width / 2 - ARROW_HALF_W}px` },
          arrowDirection: 'up',
        };
      }

      // 'end' — original behaviour: right edge of tooltip aligns with right edge of trigger.
      const rightOffset = Math.max(margin, window.innerWidth - rect.right);
      return {
        tooltipStyle: {
          top: topVal,
          right: `${rightOffset}px`,
          maxWidth: `${maxWidth}px`,
        },
        arrowStyle: { right: `${rect.width / 2 - ARROW_HALF_W}px` },
        arrowDirection: 'up',
      };
    }
  }
}
